import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import Callout from '@/components/UI/Callout';
import EmptyState from '@/components/UI/EmptyState';
import SearchSelect from '@/components/UI/SearchSelect';
import SegmentedControl from '@/components/UI/SegmentedControl';
import AlphaAtlasCombinationResults from './AlphaAtlasCombinationResults';
import {
    type AlphaAtlasDirection,
    type AlphaAtlasFilterState,
    type AlphaAtlasReportFilters,
    type AlphaAtlasReportRow,
    alphaAtlasDirectionLabel,
    alphaAtlasEntityKey,
    buildAlphaAtlasCombinationQuery
} from './AlphaAtlasReportState';
import { catalogIndexByFingerprint, catalogSelectItems, entitySelectItems } from './alphaAtlasSelectItems';

interface AlphaAtlasCombinationLookupProps {
    filters: AlphaAtlasFilterState;
    options: AlphaAtlasReportFilters | undefined;
    optionsLoading: boolean;
    queryEnabled: boolean;
    onChange: (patch: Partial<AlphaAtlasFilterState>) => void;
    onSelectRow: (row: AlphaAtlasReportRow) => void;
}

/** Builder for one supported hypothesis plus its results; shares trigger/context/direction/entity state with the report filters. */
const AlphaAtlasCombinationLookup = ({
    filters,
    options,
    optionsLoading,
    queryEnabled,
    onChange,
    onSelectRow
}: AlphaAtlasCombinationLookupProps) => {
    const parents = useMemo(() => options?.parents ?? [], [options]);
    const children = useMemo(() => options?.children ?? [], [options]);
    const parentItems = useMemo(() => catalogSelectItems(parents), [parents]);
    const childItems = useMemo(() => catalogSelectItems(children), [children]);
    const parentIndexByFingerprint = useMemo(() => catalogIndexByFingerprint(parents), [parents]);
    const childIndexByFingerprint = useMemo(() => catalogIndexByFingerprint(children), [children]);
    const entityItems = useMemo(() => entitySelectItems(options?.entities ?? []), [options]);
    const selectedEntityIndex = (options?.entities ?? []).findIndex((entity) => alphaAtlasEntityKey(entity) === filters.entityKeys[0]);

    const query = buildAlphaAtlasCombinationQuery(filters);
    const hasCombination = query.parentFingerprint !== undefined || query.childFingerprint !== undefined;
    const detail = useQuery({
        ...trpc.alphaAtlas.combinationDetail.queryOptions(query),
        enabled: hasCombination && queryEnabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData
    });

    return (
        <div className="space-y-5">
            <section aria-label="Hypothesis builder" className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[2fr_2fr_1fr_auto]">
                    <SearchSelect
                        noMargin
                        isLoading={optionsLoading}
                        items={parentItems}
                        label="Trigger"
                        mode="single"
                        placeholder="Pick a trigger event"
                        size="sm"
                        value={filters.parentFingerprint ? (parentIndexByFingerprint.get(filters.parentFingerprint) ?? null) : null}
                        onChange={(index) => onChange({ parentFingerprint: index === null ? undefined : parents[index - 1]?.fingerprint })}
                    />
                    <SearchSelect
                        noMargin
                        isLoading={optionsLoading}
                        items={childItems}
                        label="Context"
                        mode="single"
                        placeholder="Pick a context state (optional)"
                        size="sm"
                        value={filters.childFingerprint ? (childIndexByFingerprint.get(filters.childFingerprint) ?? null) : null}
                        onChange={(index) => onChange({ childFingerprint: index === null ? undefined : children[index - 1]?.fingerprint })}
                    />
                    <SearchSelect
                        noMargin
                        isLoading={optionsLoading}
                        items={entityItems}
                        label="Entities"
                        mode="single"
                        placeholder="All monitored entities"
                        size="sm"
                        value={filters.scope === 'entity' && selectedEntityIndex >= 0 ? selectedEntityIndex + 1 : null}
                        onChange={(index) => {
                            const entity = index === null ? undefined : options?.entities[index - 1];
                            onChange(
                                entity
                                    ? { scope: 'entity', entityKeys: [alphaAtlasEntityKey(entity)] }
                                    : { scope: 'pooled', entityKeys: [] }
                            );
                        }}
                    />
                    <div>
                        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">Outcome scenario</div>
                        <SegmentedControl<AlphaAtlasDirection>
                            ariaLabel="Outcome scenario"
                            options={[
                                { value: 'long', label: alphaAtlasDirectionLabel(filters.outcomeKind, 'long') },
                                { value: 'short', label: alphaAtlasDirectionLabel(filters.outcomeKind, 'short') }
                            ]}
                            value={query.direction}
                            onChange={(direction) => onChange({ direction })}
                        />
                    </div>
                </div>
                <p className="text-xs text-zinc-500">
                    Shows the historical and selection-validation windows for every horizon, next to the trigger alone and the context
                    alone. Both windows are safe to read; verification is only revealed from the Find edge table.
                </p>
            </section>

            {!hasCombination && (
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40">
                    <EmptyState
                        description="Pick a trigger, a context, or one trigger plus one context to see how that supported hypothesis behaved."
                        title="No hypothesis selected"
                    />
                </div>
            )}
            {hasCombination && detail.error && (
                <Callout title="Could not load this hypothesis" variant="error">
                    {detail.error.message}
                </Callout>
            )}
            {hasCombination && !detail.error && detail.isPending && (
                <div aria-busy="true" className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
                    {Array.from({ length: 6 }, (_, index) => (
                        <div className="h-4 animate-pulse rounded bg-zinc-800" key={index} style={{ width: `${90 - index * 8}%` }} />
                    ))}
                </div>
            )}
            {hasCombination && detail.data && (
                <div className={`transition-opacity ${detail.isPlaceholderData ? 'opacity-50' : ''}`}>
                    <AlphaAtlasCombinationResults detail={detail.data} onSelectRow={onSelectRow} />
                </div>
            )}
        </div>
    );
};

export default AlphaAtlasCombinationLookup;
